import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { DatePicker, ConfigProvider } from 'antd';
import dayjs from 'dayjs';
import zhTW from 'antd/locale/zh_TW';
import zhCN from 'antd/locale/zh_CN';
import enUS from 'antd/locale/en_US';
import { useLanguage } from '../../contexts/LanguageContext';
import WpaLayout from './WpaLayout';
import { deleteCartDoc, getCartDoc, getCartDocAmount, updateCartDoc } from './wpaCartStore';
import { formatWpaCommerceApiError } from './wpaCommerceFetch';
import {
  shouldLockWpaUnitStepOfOne,
  resolveFastQtyStepSize,
  formatQtyStepForDisplay,
  collectMoqViolations,
  buildMoqSubmitErrorMessage,
  getProductQtyStepUnitLabel,
} from './wpaProductUtils';
import useWpaDeliveryAvailability from './wpaDeliveryAvailability';

const PRIMARY = '#4CAF50';
/** 購物車明細數量列：維持原本小按鈕尺寸，全列同一字級；MOQ 鎖定僅套在 ±1 上，勿讓大步進鈕變灰 */
const QTY_CTRL_PX = 13;
const QTY_CTRL_LH = 1.2;
const QTY_CTRL_BTN = 30;
const QTY_CTRL_FONT_FAMILY =
  'system-ui, -apple-system, "Segoe UI", Roboto, "PingFang TC", "Microsoft JhengHei", sans-serif';

function lineCartQtyUnitLabel(line, t, uiLocale) {
  if (!line) return t('wpa.addToCart.defaultUnit');
  const fromLocales = getProductQtyStepUnitLabel(line, uiLocale, '');
  if (fromLocales) return fromLocales;
  const fromApi = line.unit != null && String(line.unit).trim() !== '' ? String(line.unit).trim() : '';
  if (fromApi) return fromApi;
  return t('wpa.addToCart.defaultUnit');
}

const styles = {
  card: { margin: 12, padding: 14, border: '1px solid #eee', background: '#fff' },
  row: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, padding: '10px 0', borderBottom: '1px solid #f3f3f3' },
  rowMultiline: { display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8, padding: '10px 0', borderBottom: '1px solid #f3f3f3' },
  label: { fontSize: 14, color: '#333' },
  labelRequired: { fontSize: 14, color: '#e53935' },
  value: { fontSize: 14, color: '#111', fontWeight: 600, textAlign: 'right' },
  input: { width: '65%', border: '1px solid #eee', background: '#fafafa', height: 38, padding: '0 10px', fontSize: 14 },
  inputAddress: {
    width: '65%',
    border: '1px solid #eee',
    background: '#fafafa',
    minHeight: 72,
    padding: '8px 10px',
    fontSize: 14,
    lineHeight: 1.4,
    resize: 'vertical',
    boxSizing: 'border-box',
  },
  line: { display: 'flex', alignItems: 'center', gap: 8, padding: '10px 0', borderBottom: '1px solid #f0f0f0' },
  lineInfo: { flex: 1, minWidth: 0 },
  name: { fontWeight: 600, fontSize: 14 },
  itemNo: { fontSize: 12, color: '#999' },
  price: { fontSize: 14, color: PRIMARY, fontWeight: 700 },
  qtyRow: { display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0 },
  qtyBtn: {
    width: QTY_CTRL_BTN,
    minWidth: QTY_CTRL_BTN,
    minHeight: QTY_CTRL_BTN,
    height: QTY_CTRL_BTN,
    borderRadius: 6,
    border: '1px solid #ddd',
    background: '#fff',
    color: '#111',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 0,
    boxSizing: 'border-box',
    margin: 0,
    WebkitAppearance: 'none',
    appearance: 'none',
    font: `600 ${QTY_CTRL_PX}px/${QTY_CTRL_LH} ${QTY_CTRL_FONT_FAMILY}`,
    WebkitTextSizeAdjust: '100%',
  },
  qtyWide: { width: 40, minWidth: 40, padding: '0 4px' },
  qtyText: {
    color: '#111',
    fontSize: QTY_CTRL_PX,
    fontWeight: 600,
    lineHeight: QTY_CTRL_LH,
    fontFamily: QTY_CTRL_FONT_FAMILY,
    minWidth: 28,
    padding: '0 4px',
    textAlign: 'center',
    whiteSpace: 'nowrap',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxSizing: 'border-box',
  },
  delete: {
    border: 'none',
    background: 'none',
    color: '#e53935',
    fontSize: 18,
    cursor: 'pointer',
    padding: 4,
    flexShrink: 0,
  },
  btnRow: { display: 'flex', gap: 10, padding: '0 12px' },
  btn: { flex: 1, height: 44, borderRadius: 8, border: 'none', cursor: 'pointer', fontWeight: 700 },
  btnPrimary: { background: PRIMARY, color: '#fff' },
  btnGhost: { background: '#fff', color: PRIMARY, border: `1px solid ${PRIMARY}` },
  btnDanger: { display: 'block', width: 'calc(100% - 24px)', margin: '12px', height: 42, border: '1px solid #e53935', background: '#fff', color: '#e53935', fontWeight: 700, cursor: 'pointer' },
  toast: { position: 'fixed', bottom: 24, left: '50%', transform: 'translateX(-50%)', background: 'rgba(0,0,0,0.75)', color: '#fff', padding: '8px 20px', borderRadius: 20, fontSize: 14, zIndex: 2000, whiteSpace: 'nowrap' },
  loading: { textAlign: 'center', padding: 32, color: '#999' },
  errorOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(0,0,0,0.5)',
    zIndex: 4000,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 20,
  },
  errorBox: {
    background: '#fff',
    borderRadius: 12,
    padding: 20,
    maxWidth: 340,
    width: '100%',
    maxHeight: '70vh',
    overflow: 'auto',
  },
  errorTitle: { fontSize: 16, fontWeight: 600, color: '#333', marginBottom: 12 },
  errorBody: { fontSize: 14, color: '#555', lineHeight: 1.5, whiteSpace: 'pre-wrap', wordBreak: 'break-word' },
  errorOk: {
    marginTop: 16,
    width: '100%',
    padding: '12px 0',
    background: PRIMARY,
    color: '#fff',
    border: 'none',
    borderRadius: 8,
    fontSize: 15,
    fontWeight: 600,
    cursor: 'pointer',
  },
  confirmActions: {
    marginTop: 16,
    display: 'flex',
    gap: 10,
  },
  confirmCancelBtn: {
    flex: 1,
    padding: '12px 0',
    background: '#fff',
    color: '#555',
    border: '1px solid #ddd',
    borderRadius: 8,
    fontSize: 15,
    fontWeight: 600,
    cursor: 'pointer',
  },
  confirmConfirmBtn: {
    flex: 1,
    padding: '12px 0',
    background: PRIMARY,
    color: '#fff',
    border: 'none',
    borderRadius: 8,
    fontSize: 15,
    fontWeight: 600,
    cursor: 'pointer',
  },
  confirmConfirmDanger: {
    background: '#e53935',
  },
};

/** 把目前 draft 中可保存到 cart store 的欄位整理成 patch 物件。 */
function buildCartDocPatch(draft) {
  return {
    deliveryDate: draft.deliveryDate || '',
    poNumber: draft.poNumber || '',
    remark: draft.remark || '',
    address: draft.address || '',
    lines: (draft.lines || []).map((l) => ({ ...l })),
  };
}

/** 兩份 doc 是否「對使用者來說相同」（只比關心的可編輯欄位）。 */
function isSameDraft(a, b) {
  if (!a || !b) return a === b;
  if ((a.deliveryDate || '') !== (b.deliveryDate || '')) return false;
  if ((a.poNumber || '') !== (b.poNumber || '')) return false;
  if ((a.remark || '') !== (b.remark || '')) return false;
  if ((a.address || '') !== (b.address || '')) return false;
  const la = a.lines || [];
  const lb = b.lines || [];
  if (la.length !== lb.length) return false;
  for (let i = 0; i < la.length; i++) {
    if (la[i].id !== lb[i].id) return false;
    if (Number(la[i].quantity || 0) !== Number(lb[i].quantity || 0)) return false;
  }
  return true;
}

export default function WpaCartDetailPage() {
  const { t, currentLanguage } = useLanguage();
  const { companyId, cartId } = useParams();
  const navigate = useNavigate();
  /** 進入頁面當下從 cart store 載入的原始 doc（dirty 比對用基準） */
  const [originalDoc, setOriginalDoc] = useState(null);
  /** 使用者編輯中的 draft（一切 UI 變更只動這份，不寫 cart store） */
  const [draftDoc, setDraftDoc] = useState(null);
  const [toast, setToast] = useState('');
  const [saving, setSaving] = useState(false);
  const [errorDialog, setErrorDialog] = useState(null);
  /**
   * 通用確認對話框：
   *   { title, message, confirmLabel, cancelLabel, confirmDanger?, onConfirm() }
   * 沒有時為 null。
   */
  const [confirmDialog, setConfirmDialog] = useState(null);
  /** 已決定要離開的旗標：避免 navigate 後又被自己的 dirty guard 攔下來 */
  const skipDirtyGuardRef = useRef(false);
  // 同步防重入：state(saving) 更新為非同步，毫秒級連點時兩次 click 會在 re-render 前都讀到 saving=false，
  // 導致重覆送出 POST /orders。改用 ref 在事件當下同步鎖定。
  const savingRef = useRef(false);

  const antdLocale = useMemo(() => {
    switch (currentLanguage) {
      case 'zh-SC': return zhCN;
      case 'en': return enUS;
      default: return zhTW;
    }
  }, [currentLanguage]);
  const { disabledDate, getReason, loading: availabilityLoading, extendPrefetchTo } = useWpaDeliveryAvailability({
    customerId: draftDoc?.customerId,
    enabled: draftDoc != null,
  });
  const deliveryDateStr = draftDoc?.deliveryDate ? String(draftDoc.deliveryDate).slice(0, 10) : '';
  const deliveryDateDayjs = useMemo(
    () => (deliveryDateStr ? dayjs(deliveryDateStr) : null),
    [deliveryDateStr],
  );

  const load = useCallback(() => {
    const fresh = getCartDoc(cartId);
    setOriginalDoc(fresh);
    setDraftDoc(fresh ? { ...fresh, lines: (fresh.lines || []).map((l) => ({ ...l })) } : null);
  }, [cartId]);

  useEffect(() => {
    load();
  }, [load]);

  const isDirty = useMemo(() => {
    if (!draftDoc || !originalDoc) return false;
    return !isSameDraft(draftDoc, originalDoc);
  }, [draftDoc, originalDoc]);

  /** 重新整理 / 關閉分頁時也擋一下 */
  useEffect(() => {
    if (!isDirty) return undefined;
    const handler = (e) => {
      e.preventDefault();
      e.returnValue = '';
      return '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [isDirty]);

  const total = useMemo(() => getCartDocAmount(draftDoc), [draftDoc]);
  const showToast = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(''), 1600);
  };

  const patchMeta = (patch) => {
    setDraftDoc((prev) => (prev ? { ...prev, ...patch } : prev));
  };

  /** draft 內調整 line quantity；±1 在鎖定時直接忽略。 */
  const stepLineQty = (lineId, delta) => {
    setDraftDoc((prev) => {
      if (!prev) return prev;
      const lines = (prev.lines || []).map((l) => ({ ...l }));
      const idx = lines.findIndex((l) => l.id === lineId);
      if (idx < 0) return prev;
      const line = lines[idx];
      if ((delta === 1 || delta === -1) && shouldLockWpaUnitStepOfOne(line)) {
        return prev;
      }
      const cur = Number(line.quantity || 0);
      line.quantity = Math.max(1, cur + delta);
      lines[idx] = line;
      return { ...prev, lines };
    });
  };

  /** draft 內移除某 line（不彈確認）。 */
  const removeLineInDraft = (lineId) => {
    setDraftDoc((prev) => {
      if (!prev) return prev;
      return { ...prev, lines: (prev.lines || []).filter((l) => l.id !== lineId) };
    });
  };

  /** 立即刪除整張單並離開（用於「最後一項商品移除」與「刪除訂單」按鈕）。 */
  const deleteOrderImmediately = () => {
    skipDirtyGuardRef.current = true;
    deleteCartDoc(cartId);
    navigate(`/${companyId}/wpa/cart`, { replace: true });
  };

  /**
   * 處理「按 −」會把 qty 減到 ≤ 0 的情境：
   *   - 若該訂單只有一行 → 警告會刪掉整張訂單
   *   - 否則 → 詢問是否要移除此項目
   *   - 取消 → 不變動 qty（保持原值）
   */
  const handleStepMinusGuarded = (line, delta) => {
    if (!draftDoc) return;
    if (shouldLockWpaUnitStepOfOne(line) && (delta === -1 || delta === 1)) return;
    const cur = Number(line.quantity || 0);
    const next = cur + delta;
    if (next >= 1) {
      stepLineQty(line.id, delta);
      return;
    }
    const isLastLine = (draftDoc.lines || []).length <= 1;
    if (isLastLine) {
      setConfirmDialog({
        title: t('wpa.cartDetail.confirmDeleteOrderTitle'),
        message: t('wpa.cartDetail.confirmDeleteOrderMsg'),
        confirmLabel: t('wpa.cartDetail.confirmDelete'),
        cancelLabel: t('wpa.cartDetail.confirmKeep'),
        confirmDanger: true,
        onConfirm: () => {
          setConfirmDialog(null);
          deleteOrderImmediately();
        },
      });
    } else {
      setConfirmDialog({
        title: t('wpa.cartDetail.confirmRemoveItemTitle'),
        message: t('wpa.cartDetail.confirmRemoveItemMsg'),
        confirmLabel: t('wpa.cartDetail.confirmRemove'),
        cancelLabel: t('wpa.cartDetail.confirmKeep'),
        confirmDanger: true,
        onConfirm: () => {
          setConfirmDialog(null);
          removeLineInDraft(line.id);
        },
      });
    }
  };

  /** ✕ 直接刪除某一行：多行直接移除；最後一行則彈刪整張訂單的確認。 */
  const handleDeleteLine = (line) => {
    if (!draftDoc) return;
    const isLastLine = (draftDoc.lines || []).length <= 1;
    if (isLastLine) {
      setConfirmDialog({
        title: t('wpa.cartDetail.confirmDeleteOrderTitle'),
        message: t('wpa.cartDetail.confirmDeleteOrderMsg'),
        confirmLabel: t('wpa.cartDetail.confirmDelete'),
        cancelLabel: t('wpa.cartDetail.confirmKeep'),
        confirmDanger: true,
        onConfirm: () => {
          setConfirmDialog(null);
          deleteOrderImmediately();
        },
      });
    } else {
      removeLineInDraft(line.id);
    }
  };

  /** 把 draft 寫回 cart store；返回最新 doc 或 null。 */
  const persistDraft = () => {
    if (!draftDoc) return null;
    const next = updateCartDoc(cartId, buildCartDocPatch(draftDoc));
    if (next) {
      const snapshot = { ...next, lines: (next.lines || []).map((l) => ({ ...l })) };
      setOriginalDoc(snapshot);
      setDraftDoc(snapshot);
    }
    return next;
  };

  const handleClickUpdate = () => {
    if (!draftDoc) return;
    const persisted = persistDraft();
    if (persisted) showToast(t('wpa.cartDetail.updated'));
  };

  const submit = async () => {
    if (!draftDoc || !draftDoc.lines?.length || savingRef.current || saving) return;
    const moqMsg = buildMoqSubmitErrorMessage(collectMoqViolations(draftDoc.lines), t);
    if (moqMsg) {
      setErrorDialog(moqMsg);
      return;
    }
    savingRef.current = true;
    const persisted = persistDraft();
    const submitDoc = persisted || draftDoc;
    setSaving(true);
    const token = localStorage.getItem('wpa_token');
    const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
    // 成功送出後會延遲導頁，期間維持鎖定避免空窗被連點；僅在錯誤/例外時解鎖以利重試。
    let submittedOk = false;
    try {
      const customer = JSON.parse(localStorage.getItem('wpa_current_customer') || 'null');
      const deliveryDateIso = submitDoc.deliveryDate
        ? `${String(submitDoc.deliveryDate).slice(0, 10)}T12:00:00.000Z`
        : null;

      const createRes = await fetch('/api/wpa-commerce/orders', {
        method: 'POST',
        headers,
        body: JSON.stringify({
          customerId: submitDoc.customerId || customer?.id,
          clientSubmissionKey: submitDoc.id,
          deliveryDate: deliveryDateIso,
          purchaseOrderNo: submitDoc.poNumber || null,
          remark: submitDoc.remark || null,
          deliveryAddress: submitDoc.address || null,
          lines: (submitDoc.lines || []).map((l) => ({
            productId: l.productId || null,
            itemNo: l.itemNo || '',
            productName: l.name || '',
            unitPrice: Number(l.price || 0),
            quantity: Number(l.quantity || 0),
            unit: l.unit || null,
          })),
        }),
      });

      const createJson = await createRes.json().catch(() => ({}));
      if (createRes.status === 401) {
        skipDirtyGuardRef.current = true;
        navigate(`/${companyId}/wpa/login`, { replace: true });
        return;
      }
      if (!createRes.ok) {
        setErrorDialog(formatWpaCommerceApiError(createRes, createJson, t));
        return;
      }

      const orderId = createJson.data?.id;
      if (orderId) {
        const subRes = await fetch('/api/wpa-commerce/orders/submit', {
          method: 'POST',
          headers,
          body: JSON.stringify({ orderIds: [orderId] }),
        });
        const subJson = await subRes.json().catch(() => ({}));
        if (subRes.status === 401) {
          skipDirtyGuardRef.current = true;
          navigate(`/${companyId}/wpa/login`, { replace: true });
          return;
        }
        if (!subRes.ok) {
          setErrorDialog(formatWpaCommerceApiError(subRes, subJson, t));
          return;
        }
      }

      submittedOk = true;
      skipDirtyGuardRef.current = true;
      deleteCartDoc(cartId);
      showToast(t('wpa.cartDetail.orderSubmitted'));
      setTimeout(() => navigate(`/${companyId}/wpa/cart`, { replace: true }), 600);
    } catch (err) {
      setErrorDialog(err?.message || t('wpa.cartDetail.submitFail'));
    } finally {
      if (!submittedOk) savingRef.current = false;
      setSaving(false);
    }
  };

  /** 嘗試離開頁面：dirty 時彈確認。 */
  const requestLeave = (action) => {
    if (skipDirtyGuardRef.current || !isDirty) {
      action();
      return;
    }
    setConfirmDialog({
      title: t('wpa.cartDetail.confirmLeaveTitle'),
      message: t('wpa.cartDetail.confirmLeaveMsg'),
      confirmLabel: t('wpa.cartDetail.confirmLeave'),
      cancelLabel: t('wpa.cartDetail.confirmStay'),
      confirmDanger: true,
      onConfirm: () => {
        setConfirmDialog(null);
        skipDirtyGuardRef.current = true;
        action();
      },
    });
  };

  const handleClickDeleteOrder = () => {
    setConfirmDialog({
      title: t('wpa.cartDetail.confirmDeleteOrderBtnTitle'),
      message: t('wpa.cartDetail.confirmDeleteOrderBtnMsg'),
      confirmLabel: t('wpa.cartDetail.confirmDelete'),
      cancelLabel: t('wpa.cartDetail.confirmKeep'),
      confirmDanger: true,
      onConfirm: () => {
        setConfirmDialog(null);
        deleteOrderImmediately();
      },
    });
  };

  const layoutBack = {
    leadingMode: 'back',
    onBack: () => requestLeave(() => navigate(`/${companyId}/wpa/cart`)),
  };

  if (!draftDoc) {
    return (
      <WpaLayout title={t('wpa.titles.cartDetail')} {...layoutBack}>
        <div style={styles.loading}>{t('wpa.cartDetail.notFound')}</div>
      </WpaLayout>
    );
  }

  return (
    <WpaLayout title={t('wpa.titles.cartDetail')} {...layoutBack}>
      <div style={styles.card}>
        <div style={styles.row}><span style={styles.label}>{t('wpa.cartDetail.orderNo')}</span><span style={styles.value}>{draftDoc.cartNo}</span></div>
        <div style={styles.row}><span style={styles.label}>{t('wpa.cartDetail.total')}</span><span style={styles.value} translate="no">${total.toFixed(2)}</span></div>
        <div style={styles.row}>
          <span style={styles.labelRequired}>{t('wpa.cartDetail.deliveryDate')}</span>
          <ConfigProvider locale={antdLocale}>
            <DatePicker
              style={{ width: '65%' }}
              popupClassName="wpa-delivery-picker-popup"
              value={deliveryDateDayjs}
              format="YYYY-MM-DD"
              allowClear={false}
              inputReadOnly
              disabledDate={disabledDate}
              onChange={(d) => {
                patchMeta({ deliveryDate: d ? d.format('YYYY-MM-DD') : '' });
              }}
              onPanelChange={(d) => {
                if (d) extendPrefetchTo(d);
              }}
              placeholder={availabilityLoading ? '載入中…' : ''}
            />
          </ConfigProvider>
        </div>
        {(() => {
          const reason = deliveryDateDayjs ? getReason(deliveryDateDayjs) : null;
          return reason ? (
            <div style={{ padding: '4px 0 8px', fontSize: 12, color: '#e65100' }}>{reason.message}</div>
          ) : null;
        })()}
        <div style={styles.row}><span style={styles.label}>{t('wpa.cartDetail.poNo')}</span><input style={styles.input} value={draftDoc.poNumber || ''} onChange={(e) => patchMeta({ poNumber: e.target.value })} /></div>
        <div style={styles.row}><span style={styles.label}>{t('wpa.cartDetail.remark')}</span><input style={styles.input} value={draftDoc.remark || ''} onChange={(e) => patchMeta({ remark: e.target.value })} /></div>
        <div style={styles.rowMultiline}>
          <span style={{ ...styles.label, paddingTop: 8 }}>{t('wpa.cartDetail.address')}</span>
          <div
            style={{
              ...styles.inputAddress,
              background: '#f0f0f0',
              color: '#333',
              cursor: 'default',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
            }}
          >
            {draftDoc.address?.trim() ? draftDoc.address : '-'}
          </div>
        </div>
      </div>

      <div style={styles.card}>
        {(draftDoc.lines || []).map((l) => {
          const stepSz = resolveFastQtyStepSize(l);
          const fastStep = stepSz != null ? stepSz : 10;
          const stepLabel = formatQtyStepForDisplay(fastStep);
          const lockUnitStep = shouldLockWpaUnitStepOfOne(l);
          const stepBtnStyle = { ...styles.qtyBtn, ...styles.qtyWide };
          const unitStepBtnStyle = lockUnitStep
            ? { ...styles.qtyBtn, opacity: 0.45, cursor: 'not-allowed', color: '#999' }
            : styles.qtyBtn;
          const qtyWithUom = t('wpa.addToCart.qtyWithUnitFmt', {
            qty: l.quantity,
            unit: lineCartQtyUnitLabel(l, t, currentLanguage),
          });
          return (
            <div key={l.id} style={styles.line}>
              <div style={styles.lineInfo}>
                <div style={styles.name}>{l.name}</div>
                <div style={styles.itemNo}>#{l.itemNo}</div>
                <div style={styles.price} translate="no">${Number(l.price || 0).toFixed(2)}</div>
              </div>
              <div style={styles.qtyRow}>
                <button type="button" style={stepBtnStyle} onClick={() => handleStepMinusGuarded(l, -fastStep)}>{t('wpa.addToCart.fastStepMinus', { step: stepLabel })}</button>
                <button
                  type="button"
                  style={unitStepBtnStyle}
                  disabled={lockUnitStep}
                  aria-disabled={lockUnitStep}
                  onClick={() => { if (!lockUnitStep) handleStepMinusGuarded(l, -1); }}
                >
                  {t('wpa.addToCart.unitStepMinus')}
                </button>
                <span style={styles.qtyText}>{qtyWithUom}</span>
                <button
                  type="button"
                  style={unitStepBtnStyle}
                  disabled={lockUnitStep}
                  aria-disabled={lockUnitStep}
                  onClick={() => { if (!lockUnitStep) stepLineQty(l.id, 1); }}
                >
                  {t('wpa.addToCart.unitStepPlus')}
                </button>
                <button type="button" style={stepBtnStyle} onClick={() => stepLineQty(l.id, fastStep)}>{t('wpa.addToCart.fastStepPlus', { step: stepLabel })}</button>
              </div>
              <button type="button" style={styles.delete} onClick={() => handleDeleteLine(l)}>✕</button>
            </div>
          );
        })}
      </div>

      <div style={styles.btnRow}>
        <button type="button" style={{ ...styles.btn, ...styles.btnGhost }} onClick={handleClickUpdate}>{t('wpa.cartDetail.update')}</button>
        <button type="button" style={{ ...styles.btn, ...styles.btnPrimary }} onClick={submit} disabled={saving}>{saving ? t('wpa.cartDetail.submitting') : t('wpa.cartDetail.submit')}</button>
      </div>
      <button type="button" style={styles.btnDanger} onClick={handleClickDeleteOrder}>{t('wpa.cartDetail.deleteOrder')}</button>
      {toast && <div style={styles.toast}>{toast}</div>}

      {errorDialog && (
        <div style={styles.errorOverlay} role="dialog" aria-modal="true">
          <div style={styles.errorBox}>
            <div style={styles.errorTitle}>{t('wpa.cartDetail.errorTitle')}</div>
            <div style={styles.errorBody}>{errorDialog}</div>
            <button type="button" style={styles.errorOk} onClick={() => setErrorDialog(null)}>
              {t('wpa.cartDetail.errorOk')}
            </button>
          </div>
        </div>
      )}

      {confirmDialog && (
        <div style={styles.errorOverlay} role="dialog" aria-modal="true">
          <div style={styles.errorBox}>
            <div style={styles.errorTitle}>{confirmDialog.title}</div>
            <div style={styles.errorBody}>{confirmDialog.message}</div>
            <div style={styles.confirmActions}>
              <button
                type="button"
                style={styles.confirmCancelBtn}
                onClick={() => setConfirmDialog(null)}
              >
                {confirmDialog.cancelLabel || t('wpa.cartDetail.confirmNo')}
              </button>
              <button
                type="button"
                style={{
                  ...styles.confirmConfirmBtn,
                  ...(confirmDialog.confirmDanger ? styles.confirmConfirmDanger : null),
                }}
                onClick={() => {
                  if (typeof confirmDialog.onConfirm === 'function') confirmDialog.onConfirm();
                  else setConfirmDialog(null);
                }}
              >
                {confirmDialog.confirmLabel || t('wpa.cartDetail.confirmYes')}
              </button>
            </div>
          </div>
        </div>
      )}
    </WpaLayout>
  );
}
